import _ from 'lodash'
import type { Rpts_IReportStatusUpdate, Rpts_ReportStatusId } from './Rpts.reportStatus.types'
import type { Type_DisplayUnitUuid } from '../Type/Type.hive'
import * as sentry from '../../src/utils/sentry'
import {
    IDB_reportStatusUpdatesDelete,
    IDB_reportStatusUpdatesReadAll,
    IDB_reportStatusUpdatesWrite,
} from '../IDB/IDB.table.reportStatus'

export async function Rpts_addStatusLoc(
    originActivity: number,
    statusId: Rpts_ReportStatusId,
    deps: {
        IDB_reportStatusUpdatesReadAll: typeof IDB_reportStatusUpdatesReadAll
        IDB_reportStatusUpdatesWrite: typeof IDB_reportStatusUpdatesWrite
    } = {
        IDB_reportStatusUpdatesReadAll,
        IDB_reportStatusUpdatesWrite,
    }
) {
    const statuses = await deps.IDB_reportStatusUpdatesReadAll()
    const matchingStatus = statuses.find((rstatus) => rstatus.originActivity === originActivity)
    if (matchingStatus) {
        return deps.IDB_reportStatusUpdatesWrite([
            {
                ...matchingStatus,
                timestamp: new Date(),
                statusId,
            },
        ])
    }

    return deps.IDB_reportStatusUpdatesWrite([
        {
            timestamp: new Date(),
            statusId,
            originActivity,
        },
    ])
}

export async function Rpts_removeStatusLoc(
    originActivity: number,
    deps: {
        IDB_reportStatusUpdatesReadAll: typeof IDB_reportStatusUpdatesReadAll
        IDB_reportStatusUpdatesDelete: typeof IDB_reportStatusUpdatesDelete
    } = {
        IDB_reportStatusUpdatesReadAll,
        IDB_reportStatusUpdatesDelete,
    }
) {
    const statuses = await deps.IDB_reportStatusUpdatesReadAll()
    const toDelete = statuses.filter((s) => s.originActivity === originActivity).map((s) => s.id)

    return deps.IDB_reportStatusUpdatesDelete(toDelete)
}

function hasOriginUnits(status: Rpts_IReportStatusUpdate) {
    return status.hasOwnProperty('originUnits')
}

// with the locations we only need to track one status per activity
// but for the units since we are also tracking which units they came from,
// we want one per activity-status combo
export async function Rpts_addStatusUnits(
    originActivity: number,
    unitIds: Type_DisplayUnitUuid[],
    statusId: Rpts_ReportStatusId,
    deps: {
        isTest: boolean
        IDB_reportStatusUpdatesReadAll: typeof IDB_reportStatusUpdatesReadAll
        IDB_reportStatusUpdatesWrite: typeof IDB_reportStatusUpdatesWrite
    } = {
        isTest: false,
        IDB_reportStatusUpdatesReadAll,
        IDB_reportStatusUpdatesWrite,
    }
) {
    // There are some unresolved issues regarding unit pfs with more than one call to setStatus
    // Until we figure out how to resolve them we want to disable this functionality for unit pfs
    if (!deps.isTest) return

    const statuses = await deps.IDB_reportStatusUpdatesReadAll()
    const matchingStatus = statuses.find((rs) => rs.originActivity === originActivity && rs.statusId === statusId)

    if (matchingStatus) {
        if (!hasOriginUnits(matchingStatus)) {
            return sentry.err(new Error('reportStatus is invalid'), { extra: { matchingStatus } })
        }

        return deps.IDB_reportStatusUpdatesWrite([
            {
                ...matchingStatus,
                timestamp: new Date(),
                originActivity,
                originUnits: _.uniq(matchingStatus.originUnits.concat(unitIds)),
            },
        ])
    }

    return deps.IDB_reportStatusUpdatesWrite([
        {
            timestamp: new Date(),
            statusId,
            originActivity,
            originUnits: unitIds,
        },
    ])
}

export async function Rpts_removeStatusUnits(
    originActivity: number,
    unitIds: Type_DisplayUnitUuid[],
    deps: {
        IDB_reportStatusUpdatesReadAll: typeof IDB_reportStatusUpdatesReadAll
        IDB_reportStatusUpdatesDelete: typeof IDB_reportStatusUpdatesDelete
        IDB_reportStatusUpdatesWrite: typeof IDB_reportStatusUpdatesWrite
    } = {
        IDB_reportStatusUpdatesReadAll,
        IDB_reportStatusUpdatesDelete,
        IDB_reportStatusUpdatesWrite,
    }
) {
    const statuses = await deps.IDB_reportStatusUpdatesReadAll()

    const statusesToDelete: number[] = []
    const statusesToUpdate: Rpts_IReportStatusUpdate[] = []

    statuses.forEach((status) => {
        if (status.originActivity !== originActivity) return
        if (!hasOriginUnits(status)) return sentry.err(new Error('reportStatus is invalid'), { extra: { status } })

        const newUnitIds = _.without(status.originUnits, ...unitIds)

        if (newUnitIds.length > 0) {
            statusesToUpdate.push({
                ...status,
                originUnits: newUnitIds,
            })
        } else {
            statusesToDelete.push(status.id)
        }
    })

    await deps.IDB_reportStatusUpdatesDelete(statusesToDelete)
    await deps.IDB_reportStatusUpdatesWrite(statusesToUpdate)
}

export async function Rpts_getFinalStatus(
    activityOrder: number[],
    deps: { IDB_reportStatusUpdatesReadAll: typeof IDB_reportStatusUpdatesReadAll } = {
        IDB_reportStatusUpdatesReadAll,
    }
) {
    const statusUpdates = await deps.IDB_reportStatusUpdatesReadAll()

    const orderedUpdates = activityOrder
        .map((activityId) => _.find(statusUpdates, { originActivity: activityId }))
        .filter((statusUpdate) => statusUpdate !== undefined)

    if (orderedUpdates.length === 0) return null

    return _.last(orderedUpdates).statusId
}

export async function Rpts_clearReportStatuses() {
    const ids = (await IDB_reportStatusUpdatesReadAll()).map((status) => status.id)
    await IDB_reportStatusUpdatesDelete(ids)
}
